/**
 * =============================================================================
 * AccessibleModal — A-0 accessible-dialog primitive
 * (ADR-025 Wave A / BL-ADMIN-MODAL-DIALOG-NAME-001; design: S-1 spike memo §5)
 * =============================================================================
 *
 * WHY: `@bigcommerce/big-design@1.8` places `aria-labelledby` on the modal
 * CONTENT wrapper, never on the `role="dialog"` node, and forwards no `aria-*`
 * to that node — so every big-design `Modal` fails axe `aria-dialog-name`
 * (a library-wide WCAG 2.2 AA gap; not fixable via component props; no upstream
 * release through 3.2.0 fixes it — see the S-1 memo). This thin wrapper renders
 * the SAME big-design `Modal` (inheriting its focus-trap, `closeOnEscKey`, and
 * visuals unchanged) and adds exactly two things:
 *
 *   1. A GUARANTEED ACCESSIBLE NAME. Post-mount, via a ref-callback on an anchor
 *      node inside the dialog subtree:
 *        · Strategy A — promote big-design's own generated header id (read off
 *          the inner content wrapper's `aria-labelledby`) onto the `role="dialog"`
 *          node, so the dialog's name becomes the header text.
 *        · Strategy B — when no string `header` is supplied (the only reachable
 *          unnamed case; the type is `string | undefined`), set `aria-label`
 *          from the required `label` prop.
 *      A dev-time invariant throws when neither a string `header` nor a `label`
 *      is supplied, so an unnamed dialog cannot ship.
 *
 *   2. CENTRALIZED FOCUS RESTORATION. Captures the trigger focused just before
 *      open and restores focus to it (or to `restoreFocusTo`) on the
 *      open→closed transition — the proven `ReplyImageStrip` pattern, hoisted
 *      here so call sites drop their bespoke effect. Guards a detached trigger
 *      (`document.contains`) so focus never falls to `<body>`.
 *
 * A ref CALLBACK (not `useEffect`) is used for naming because big-design portals
 * the dialog from its own effect, which races a parent effect; the callback
 * fires exactly when the anchor (hence the committed dialog subtree) attaches.
 *
 * Removable when upstream fixes the placement: the `setAttribute` is idempotent,
 * so a fixed big-design makes this wrapper an inert no-op before it is deleted.
 * =============================================================================
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Modal } from '@bigcommerce/big-design';

export interface AccessibleModalProps extends React.ComponentProps<typeof Modal> {
  /**
   * Accessible-name fallback. REQUIRED when no string `header` is supplied
   * (big-design types `header` as `string | undefined`, so the only reachable
   * unnamed case is an omitted header — ext-A7; without one, no id-bearing
   * header element exists to point the dialog at). Ignored when a string
   * `header` is present (the header text names the dialog).
   */
  label?: string;
  /**
   * Where to send focus after close. Defaults to the element focused just
   * before open (captured internally). Provide this when the trigger unmounts
   * on the same action (e.g. a row removed after Approve/Reject) so focus never
   * falls to `<body>`.
   */
  restoreFocusTo?: React.RefObject<HTMLElement> | (() => HTMLElement | null);
  /**
   * Consulted ONLY when the primary restore target (`restoreFocusTo` if given,
   * else the captured trigger) is DETACHED at restore time — e.g. a confirmed
   * action unmounted the triggering row, or closed the containing surface.
   * Never overrides a live primary target (peer-review F2). Point it at a
   * stable control (a toolbar/refresh button) so focus never falls to <body>.
   */
  fallbackFocusTo?: React.RefObject<HTMLElement> | (() => HTMLElement | null);
}

export function AccessibleModal({
  label,
  restoreFocusTo,
  fallbackFocusTo,
  children,
  ...modalProps
}: AccessibleModalProps): React.ReactElement {
  const header = modalProps.header;
  const isOpen = Boolean(modalProps.isOpen);

  // Dev/test invariant: an unnamed dialog is a programming error and must not
  // reach a review. (No-op in production builds.)
  if (import.meta.env.DEV && typeof header !== 'string' && !label) {
    throw new Error(
      'AccessibleModal: a dialog must have an accessible name — pass a string `header` or a `label`.'
    );
  }

  // --- Guaranteed accessible name (runs once, when the anchor attaches) ---
  const nameDialog = useCallback(
    (node: HTMLSpanElement | null): void => {
      if (!node) return;
      const dialog = node.closest('[role="dialog"]');
      if (!dialog) return;
      const doc = dialog.ownerDocument;
      // Strategy A: big-design already generated a header element with an id and
      // pointed the inner content wrapper's `aria-labelledby` at it. Promote that
      // same id onto the dialog node.
      const inner = dialog.querySelector('[aria-labelledby]');
      const headerId = inner?.getAttribute('aria-labelledby');
      if (headerId && doc.getElementById(headerId)) {
        dialog.setAttribute('aria-labelledby', headerId);
        return;
      }
      // Strategy B: no id-bearing header → name the dialog from `label`.
      if (label) dialog.setAttribute('aria-label', label);
    },
    [label]
  );

  // --- Focus restoration ---
  const lastFocusedWhileClosed = useRef<HTMLElement | null>(null);
  const capturedTrigger = useRef<HTMLElement | null>(null);
  const prevOpen = useRef(false);
  const restoreTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // While closed, remember the focused element (the likely trigger). Snapshotted
  // at the open transition below, so close-time restore never races this.
  useEffect(() => {
    if (isOpen) return undefined;
    const record = (): void => {
      lastFocusedWhileClosed.current =
        (document.activeElement as HTMLElement | null) ?? null;
    };
    record();
    document.addEventListener('focusin', record);
    return () => document.removeEventListener('focusin', record);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      capturedTrigger.current = lastFocusedWhileClosed.current;
      if (restoreTimer.current) {
        clearTimeout(restoreTimer.current);
        restoreTimer.current = null;
      }
    } else if (!isOpen && prevOpen.current) {
      const explicit =
        typeof restoreFocusTo === 'function'
          ? restoreFocusTo()
          : restoreFocusTo?.current ?? null;
      const primary = explicit ?? capturedTrigger.current;
      const fallback =
        typeof fallbackFocusTo === 'function'
          ? fallbackFocusTo()
          : fallbackFocusTo?.current ?? null;
      // The node big-design's focus-trap will RETURN focus to on deactivate —
      // A-0 is authoritative over that return (an explicit `restoreFocusTo`
      // must win), so the watch may override it until A-0's first successful
      // application; anything else that takes focus is a real owner.
      const trapReturnTarget = capturedTrigger.current;
      capturedTrigger.current = null;
      if (primary || fallback) {
        // Defer past big-design/focus-trap's OWN (deferred) return-focus: the
        // trap restores focus to the original trigger on deactivate, which would
        // otherwise overwrite an explicit `restoreFocusTo`. Scheduling after it
        // (the trap's timer is registered first, in the child effect) makes A-0's
        // target authoritative. Liveness is re-checked per ATTEMPT, inside the
        // timer: a live primary wins; a detached primary falls back to
        // `fallbackFocusTo` (peer-review F2 — e.g. the trigger row unmounted
        // after a confirmed action); if both are gone, no-op rather than
        // focusing <body>.
        //
        // BOUNDED RESTORE WATCH (CI root-cause fix). A one-shot `focus()` has
        // TWO orphaning races on slow machines (both observed on 2-core CI
        // runners; real keyboard/SR drops, not just test flake):
        //   1. The target is momentarily unfocusable — e.g. a Refresh control
        //      still `disabled` while the post-action refetch settles — and
        //      `focus()` silently no-ops.
        //   2. The restore SUCCEEDS onto the still-attached trigger, then the
        //      delayed refetch commit unmounts it — focus drops to <body>
        //      AFTER a "successful" restore.
        // So instead of a fire-and-forget attempt, run a short bounded WATCH:
        // each tick re-resolves liveness primary-first and (re)applies focus
        // whenever it is ORPHANED (<body>); it idles while a node WE focused
        // still holds focus (watching for race 2); and it stops permanently
        // the moment any OTHER element genuinely owns focus (a newly opened
        // dialog's trap, real user interaction) — it never fights a real
        // focus owner, which is what makes the longer horizon safe.
        const WATCH_TICKS = 30;
        const WATCH_INTERVAL_MS = 100;
        let lastFocusedByUs: HTMLElement | null = null;
        const tick = (n: number): void => {
          restoreTimer.current = null;
          const live =
            primary && document.contains(primary)
              ? primary
              : fallback && document.contains(fallback)
                ? fallback
                : null;
          if (!live) return; // nothing restorable — never focus <body>
          const holder = document.activeElement as HTMLElement | null;
          const orphaned =
            !holder || holder === document.body || holder === document.documentElement;
          // The trap's own deferred return (to the original trigger) is
          // overridable until A-0's first successful application — that is
          // what makes an explicit `restoreFocusTo` authoritative.
          const overridableTrapReturn =
            lastFocusedByUs === null && holder !== null && holder === trapReturnTarget;
          if (orphaned || overridableTrapReturn) {
            live.focus?.();
            if (document.activeElement === live) lastFocusedByUs = live;
          } else if (holder !== live && holder !== lastFocusedByUs) {
            return; // someone else owns focus — stop, do not steal
          }
          if (n < WATCH_TICKS) {
            restoreTimer.current = setTimeout(() => tick(n + 1), WATCH_INTERVAL_MS);
          }
        };
        restoreTimer.current = setTimeout(() => tick(0), 0);
      }
    }
    prevOpen.current = isOpen;
  }, [isOpen, restoreFocusTo, fallbackFocusTo]);

  // Cancel a pending restore if the modal unmounts first.
  useEffect(
    () => () => {
      if (restoreTimer.current) clearTimeout(restoreTimer.current);
    },
    []
  );

  return (
    <Modal {...modalProps}>
      <span ref={nameDialog} data-accessible-modal-anchor hidden />
      {children}
    </Modal>
  );
}
